/* eslint-disable import/no-cycle */
/* eslint-disable no-underscore-dangle */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { batch } from 'react-redux';
import merge from 'lodash/merge';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import { normalize } from 'normalizr';

import remoteTimeNow from 'libs/remote-time-now';
import { selectLocale } from 'models/localization';
import fetchBoothMetaById from 'network/fetchBoothMetaById';
import fetchBoothDesignElementsById from 'network/fetchBoothDesignElements';
import fetchBoothDesignCanvasById from 'network/fetchBoothDesignCanvas';
import fetchBoothNameByIdRequest from 'network/fetchBoothNameById';
import fetchBoothRootContentElementById from 'network/fetchBoothRootContentElementById';
import fetchMetaBoothsRequest from 'network/fetchMetaBooths';
import {
  BoothFetchedLevel,
  isFetchingLevelHigherThanExpected,
  Booth,
  BoothFetchingState,
  boothEntity,
} from './types';
import {
  selectBoothById,
  boothsAdapter,
  selectBoothPageInfo,
} from './selectors';
import { selectXtraEvent } from '../events/selectors';
import { selectSelectedCategoryIds } from '../boothTaxonomies/selectors';
import {
  setBoothTabs,
  fetchBoothTabById,
} from '../boothTabs';
import { fetchCachedCMSData } from '../common/thunks';

interface FetchBoothByIdProps {
  boothId: string;
  expectedLevel?: BoothFetchedLevel;
}

export const fetchBoothNameById = createAsyncThunk(
  'booths/fetchBoothNameById',
  async (boothId: string, { getState }) => {
    const state = getState();
    const locale = selectLocale(state);
    const boothWithNameOnly = await fetchBoothNameByIdRequest(boothId, [locale]);
    return boothWithNameOnly;
  },
);

export const fetchBoothById = createAsyncThunk(
  'booths/fetchBoothById',
  async ({
    boothId,
    expectedLevel = BoothFetchedLevel.Details,
  }: FetchBoothByIdProps, { getState, dispatch }) => {
    const state = getState();
    const locale = selectLocale(state);
    const hasProducts = (getState() as any).eventConfigs.features.includes('productPage');
    const useV2DesignElement = selectXtraEvent(state)?.features?.includes('V2_DESIGN_ELEMENT');
    const fetchedLevel = selectBoothById(state, boothId)?.$fetchedLevel || [];

    const fetchBoothDesignElements = useV2DesignElement ? fetchBoothDesignCanvasById : fetchBoothDesignElementsById;

    const rules: [BoothFetchedLevel, any][] = [
      [BoothFetchedLevel.Meta, fetchBoothMetaById],
      [BoothFetchedLevel.Design, fetchBoothDesignElements],
      [BoothFetchedLevel.Details, fetchBoothRootContentElementById],
    ];

    const tasks = rules
      .filter(([level]) => (
        !isFetchingLevelHigherThanExpected(level, expectedLevel)
        && !fetchedLevel.find((it) => it.level === level)?.locales?.includes(locale)
      ))
      .map(async ([level, action]) => [
        level,
        await action({
          boothId, locales: [locale], hasProducts,
        }),
      ]);

    const result = (await Promise.all(tasks))
      .reduce((sum, [level, next]) => {
        const sumFetchedLevel = [...sum.$fetchedLevel];
        if (!sumFetchedLevel.find((it) => it.level === level)) {
          sumFetchedLevel.push({ level, locales: [] });
        }
        const updatedFetchedLevel = sumFetchedLevel.map((it) => {
          if (it.level === level) return { ...it, locales: [...(it.locales || []), locale] };
          return it;
        });
        return { ...merge(sum, next), $fetchedLevel: updatedFetchedLevel };
      }, { $lastReceivedAt: remoteTimeNow().toISOString(), $fetchedLevel: fetchedLevel });

    const { entities } = normalize<Booth>([(selectBoothById(getState(), boothId) || { id: boothId }), result], [boothEntity]);
    if (entities.boothTabs) {
      batch(() => {
        dispatch(setBoothTabs(entities.boothTabs));
        Object.keys(entities.boothTabs).forEach((tabId) => {
          dispatch(fetchBoothTabById({ boothId, boothTabId: tabId, locale }));
        });
      });
    }
    return entities.booths || {};
  },
  {
    condition({ boothId, expectedLevel }, { getState }) {
      const state = getState();
      const locale = selectLocale(state);
      const booth = selectBoothById(state, boothId);
      const fetchingState = booth?.$fetchingState;
      switch (fetchingState) {
        case BoothFetchingState.Rejected: return false;
        case BoothFetchingState.Pending: return false;
        case BoothFetchingState.Fulfilled: {
          return !booth?.$fetchedLevel?.find((it) => it.level === expectedLevel && it.locales.includes(locale));
        }
        default: return true;
      }
    },
  },
);

export const fetchMetaBooths = createAsyncThunk(
  'booths/fetchMetaBooths',
  async ({ boothCount, boothsCursor, continueFetching = false }: any = {}, { getState }) => {
    const state = getState();
    const locale = selectLocale(state);
    const selectedCategoryIds = selectSelectedCategoryIds(state);
    const response = await fetchMetaBoothsRequest({
      categoryIds: selectedCategoryIds,
      locales: [locale],
      cursor: boothsCursor,
      count: boothCount,
    });

    const { pageInfo, totalCount } = response.boothsByFilter;

    const now = remoteTimeNow().toISOString();
    const nodes = response.boothsByFilter.nodes
      .map((node) => {
        const fetchedLevel = [...(selectBoothById(state, node.id)?.$fetchedLevel || [])];
        if (!fetchedLevel.find((it) => it.level === BoothFetchedLevel.Meta)) {
          fetchedLevel.push({ level: BoothFetchedLevel.Meta, locales: [] });
        }
        const updatedFetchedLevel = fetchedLevel.map((it) => {
          if (it.level === BoothFetchedLevel.Meta) return { ...it, locales: [...it.locales, locale] };
          return it;
        });

        return ({
          ...node,
          $fetchedLevel: updatedFetchedLevel,
          $lastReceivedAt: now,
        });
      });

    const sources = nodes.map((it: any) => selectBoothById(getState(), it.id) || { id: it.id });

    const { entities } = normalize<Booth>([...sources, ...nodes], [boothEntity]);

    return {
      booths: entities.booths || {},
      pageInfo: {
        ...pageInfo,
        fetching: continueFetching && pageInfo.hasNextPage,
      },
      selectedCategoryIds,
      totalCount,
    };
  },
);

export const fetchMoreMetaBooths = createAsyncThunk(
  'booths/fetchMoreMetaBooths',
  async (_, { getState, dispatch }) => {
    const state = getState();
    const pageInfo = selectBoothPageInfo(state);
    return dispatch(fetchMetaBooths({
      boothsCursor: pageInfo.endCursor,
      boothCount: 10,
    }));
  },
  {
    condition(_, { getState }) {
      const state = getState();
      const pageInfo = selectBoothPageInfo(state);
      return pageInfo.hasNextPage && !pageInfo.fetching;
    },
  },
);

export const boothsSlice = createSlice({
  name: 'booths',
  initialState: boothsAdapter.getInitialState({
    fetching: true,
    pageInfo: {
      hasNextPage: false,
      endCursor: null,
      fetching: false,
    },
    filteredBoothIds: [],
    totalFilteredBoothsCount: 0,
    currentSelectedCategoryIds: [],
  }),
  reducers: {
    setPageInfoFetching(state, action: PayloadAction<boolean>) {
      state.pageInfo.fetching = action.payload;
    },
    resetFilteredBoothIds(state) {
      state.filteredBoothIds = [];
      state.totalFilteredBoothsCount = 0;
      state.currentSelectedCategoryIds = [];
      state.pageInfo.endCursor = null;
      state.pageInfo.hasNextPage = false;
    },
    setBoothLivestreams(state, action: PayloadAction<{ boothId: string; livestreams: any[] }>) {
      const { boothId, livestreams } = action.payload;
      if (state.entities[boothId]) {
        state.entities[boothId].livestreams = livestreams;
      }
    },
    setBoothVideoCallUrl(state, action: PayloadAction<{ boothId: string; videoCallUrl: string }>) {
      const { boothId, videoCallUrl } = action.payload;
      if (state.entities[boothId]) {
        state.entities[boothId].videoCallUrl = videoCallUrl;
      }
    },
    setBoothHasAttachment(state, action: PayloadAction<{ boothId: string; hasAttachment: boolean }>) {
      const { boothId, hasAttachment } = action.payload;
      if (state.entities[boothId]) {
        state.entities[boothId].hasAttachment = hasAttachment;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCachedCMSData.rejected, (state) => {
      if (state.fetching) state.fetching = false;
    });
    builder.addCase(fetchCachedCMSData.fulfilled, (state, action) => {
      boothsAdapter.upsertMany(state, action.payload.booths);
      state.ids = action.payload.boothIds;
      state.fetching = false;
      state.pageInfo = {
        hasNextPage: false,
        endCursor: null,
        fetching: false,
      };
      state.currentSelectedCategoryIds = [];
    });

    builder.addCase(fetchBoothById.pending, (state, action) => {
      const { boothId } = action.meta.arg;
      boothsAdapter.upsertOne(state, { id: boothId, $fetchingState: BoothFetchingState.Pending } as any);
    });
    builder.addCase(fetchBoothById.rejected, (state, action) => {
      const { boothId } = action.meta.arg;
      boothsAdapter.upsertOne(state, { id: boothId, $fetchingState: BoothFetchingState.Rejected } as any);
    });
    builder.addCase(fetchBoothById.fulfilled, (state, action) => {
      const { boothId } = action.meta.arg;
      if (!action.payload[boothId]) {
        boothsAdapter.upsertOne(state, { id: boothId, $fetchingState: BoothFetchingState.Rejected } as any);
        return;
      }
      boothsAdapter.upsertOne(state, { ...action.payload[boothId], $fetchingState: BoothFetchingState.Fulfilled });
    });

    builder.addCase(fetchMetaBooths.pending, (state) => {
      state.pageInfo.fetching = true;
    });
    builder.addCase(fetchMetaBooths.rejected, (state) => {
      if (state.fetching) state.fetching = false;
      state.pageInfo.fetching = false;
    });
    builder.addCase(fetchMetaBooths.fulfilled, (state, action) => {
      if (Object.keys(action.payload).length === 0) {
        state.fetching = false;
        state.pageInfo.hasNextPage = false;
        state.pageInfo.fetching = false;
        return;
      }
      state.fetching = false;

      const {
        booths,
        selectedCategoryIds,
        totalCount,
        pageInfo,
      } = action.payload;
      if (isEqual(state.currentSelectedCategoryIds, selectedCategoryIds)) {
        state.filteredBoothIds = uniq([
          ...state.filteredBoothIds,
          ...Object.keys(booths),
        ]);
      } else {
        state.filteredBoothIds = Object.keys(booths);
        state.currentSelectedCategoryIds = selectedCategoryIds;
      }
      state.totalFilteredBoothsCount = totalCount;
      state.pageInfo = pageInfo;

      boothsAdapter.upsertMany(state, booths || {});
      boothsAdapter.updateMany(state, Object.keys(booths || {}).map((id) => ({ id, changes: { $fetchingState: BoothFetchingState.Fulfilled } })));
    });
    builder.addCase(fetchBoothNameById.fulfilled, (state, action) => {
      boothsAdapter.upsertOne(state, action.payload as any);
    });
  },
});

export default boothsSlice.reducer;
export const {
  resetFilteredBoothIds,
  setBoothLivestreams,
  setBoothVideoCallUrl,
  setBoothHasAttachment,
} = boothsSlice.actions;
